import { Trash2, Loader2 } from "lucide-react";
import { Waveform } from "./Waveform";
import { stepLabel, fmtTime } from "@/lib/music";
import { useDrag } from "./useDrag";
import { LoopBand } from "./LoopBand";

const Toggle = ({ on, onClick, activeCls, children, testId }) => (
  <button
    data-testid={testId}
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    className={`w-7 h-7 text-[11px] font-mono-vox font-bold border transition-colors ${
      on ? activeCls : "border-[#352d40] text-stone-500 hover:text-stone-200"
    }`}
  >
    {children}
  </button>
);

export const TrackLane = ({ track, index, buffer, duration, progress, selected, onSelect, onUpdate, onDelete, onSeek, onDragEnd, loop }) => {
  const badge =
    track.kind === "backing"
      ? "SONG"
      : track.mode === "diatonic"
        ? `${stepLabel(track.degrees).toUpperCase()} · IN KEY`
        : track.semitones === 0
          ? "LEAD"
          : `${track.semitones > 0 ? "+" : ""}${track.semitones}`;
  const drag = useDrag({
    onDown: () => ({ start: track.start || 0, dur: duration }),
    onDrag: (c, _f0, _f1, dx) => onUpdate({ start: Math.max(0, +(c.start + dx * c.dur).toFixed(3)) }),
    onEnd: onDragEnd,
    onClick: onSeek,
  });
  return (
    <div
      data-testid={`track-lane-${index}`}
      onClick={onSelect}
      className={`flex h-24 border transition-colors rise ${
        selected ? "border-[#D99B43]/70 bg-[#161319]" : "border-[#352d40] bg-[#0f0d11] hover:border-[#5a1e28]"
      }`}
    >
      <div className="w-52 shrink-0 border-r border-[#352d40] p-3 flex flex-col justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-1.5 h-8 shrink-0" style={{ background: track.color, boxShadow: `0 0 8px ${track.color}` }} />
          <div className="min-w-0">
            <p className="text-[10px] font-mono-vox tracking-[0.2em]" style={{ color: track.color }}>
              {badge}
            </p>
            <p className="text-sm truncate text-stone-200" data-testid={`track-name-${index}`}>
              {track.name}
            </p>
            {track.start > 0 && (
              <p className="text-[10px] font-mono-vox text-stone-500" data-testid={`track-start-${index}`}>@ {fmtTime(track.start)}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <Toggle testId={`btn-track-mute-${index}`} on={track.mute} activeCls="bg-[#D97706] border-[#D97706] text-black" onClick={() => onUpdate({ mute: !track.mute })}>
            M
          </Toggle>
          <Toggle testId={`btn-track-solo-${index}`} on={track.solo} activeCls="bg-[#E11D48] border-[#E11D48] text-white" onClick={() => onUpdate({ solo: !track.solo })}>
            S
          </Toggle>
          <span className="flex-1" />
          <button
            data-testid={`btn-track-delete-${index}`}
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="p-1.5 text-stone-500 hover:text-[#E11D48] transition-colors"
            aria-label="Delete track"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>
      <div data-testid={`track-clip-area-${index}`} className="relative flex-1 cursor-grab active:cursor-grabbing touch-none select-none" {...drag} style={{ opacity: track.mute ? 0.35 : 1 }}>
        <LoopBand loop={loop} duration={duration} tall />
        {buffer ? (
          <Waveform buffer={buffer} color={track.color} offsetFrac={duration ? (track.start || 0) / duration : 0} widthFrac={duration ? buffer.duration / duration : 1} testId={`track-waveform-canvas-${index}`} />
        ) : (
          <div className="absolute inset-0 flex items-center gap-2 px-4 eyebrow">
            <Loader2 size={14} className="animate-spin text-[#D99B43]" /> Transmuting voice…
          </div>
        )}
        <div className="absolute top-0 bottom-0 w-px bg-[#EF4444] shadow-[0_0_8px_rgba(239,68,68,0.8)] pointer-events-none" style={{ left: `${progress * 100}%` }} />
      </div>
    </div>
  );
};
